import {useDisclosure} from '@sollapay/ui/hooks';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {isPackageIncomplete, isPackageInProgress, resolveFirstIncompleteStep} from '../utils';
import {usePackageFormModal} from './usePackageFormModal';
import {useSubmitTrustAccountMutation} from './useTrustAccountActivation';

import type {ActivationPackageViewModel} from './useActivationViewModel';
import type {PackageType} from '@sollapay/enums';

export interface UseActivationFlowParams {
  trustAccountId: string;
  packages: ActivationPackageViewModel[];
  canSendPackages: boolean;
  isTrustActive: boolean;
}

interface SelectedPackage {
  type: PackageType;
  initialStep?: string;
}

/**
 * Owns the two independent modal flows on the activation page — "confirm & submit" and
 * "open a package's form" — plus the CTA dispatch logic that picks between them. Kept as
 * one hook (not two) because `handleGlobalCta` routes into both depending on readiness.
 */
export function useActivationFlow({
  trustAccountId,
  packages,
  canSendPackages,
  isTrustActive
}: UseActivationFlowParams) {
  const navigate = useNavigate();
  const strategy = useTrustTypeStrategy();
  const flowModal = useDisclosure();
  const packageFormModal = usePackageFormModal(trustAccountId);
  const submitTrustAccountMutation = useSubmitTrustAccountMutation(trustAccountId);
  const [isActivationSuccess, setIsActivationSuccess] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<SelectedPackage | null>(null);

  const hasProgress = packages.some(isPackageInProgress);

  const handlePackageNavigate = (packageType: PackageType) => {
    const pkg = packages.find(p => p.type === packageType);
    setSelectedPackage({
      type: packageType,
      initialStep: pkg
        ? resolveFirstIncompleteStep(packageType, pkg.readiness.missing, strategy)
        : undefined
    });
    packageFormModal.open();
  };

  const handleGlobalCta = () => {
    if (isTrustActive) {
      navigate(`/trust-accounts/${trustAccountId}`);
      return;
    }
    if (canSendPackages) {
      setIsActivationSuccess(false);
      flowModal.open();
    } else {
      const firstIncomplete = packages.find(isPackageIncomplete);
      if (firstIncomplete) handlePackageNavigate(firstIncomplete.type);
    }
  };

  const handleConfirmSend = () => {
    submitTrustAccountMutation.mutate(undefined, {
      onSuccess: () => setIsActivationSuccess(true)
    });
  };

  const handleCloseFlowModal = () => {
    setIsActivationSuccess(false);
    flowModal.close();
  };

  return {
    hasProgress,
    isSubmitting: submitTrustAccountMutation.isPending,
    handlePackageNavigate,
    handleGlobalCta,
    flowModal: {
      isOpen: flowModal.isOpen,
      isActivationSuccess,
      onConfirm: handleConfirmSend,
      onClose: handleCloseFlowModal
    },
    packageFormModal: {
      isOpen: packageFormModal.isOpen,
      onClose: packageFormModal.close,
      initialTab: selectedPackage?.type,
      initialStep: selectedPackage?.initialStep
    }
  };
}
